import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext, type SessionUser } from "./api";
import { holenMitAblage } from "./ablage";
import { AblageHinweis } from "./netzstand";
import { alsStunden, heute } from "./datum";
import { objektAuswahl } from "./objektname";
import { alsUhrzeit, bahnen, begrenzen, minuten, zeitraum, zielzeit } from "./planlogik";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Seitenkopf, Textknopf } from "./ui";

/**
 * Der Dienstplan.
 *
 * Zwei Ansichten auf dieselben Daten, und die Wahl dazwischen ist keine
 * Geschmacksfrage.
 *
 * **Tag.** Eine Spalte je Mitarbeiter, die Uhrzeit laeuft von oben nach unten.
 * Hier wird geplant. Sechs Termine je Mitarbeiter und vier Mitarbeiter sind
 * vierundzwanzig Termine, und die passen so auf einen Bildschirm, weil jeder
 * dort steht, wo er zeitlich hingehoert. Untereinander gestapelt waeren es
 * sechstausend Pixel, gemessen an der Fassung davor.
 *
 * **Woche.** Sieben Tage als Zusammenfassung: wie viele Termine, wer, von wann
 * bis wann. Kein einziger Termin im Volltext. Das ist der Blick fuer die
 * Frage "wo ist noch Luft", nicht fuer die Arbeit am einzelnen Termin. Ein
 * Klick auf einen Tag oeffnet ihn.
 *
 * **Ein Termin traegt ein Team.** Vier Stunden Garten sind erfuellt durch
 * einen Gaertner vier Stunden lang oder durch zwei Gaertner zwei Stunden lang.
 * Deshalb steht derselbe Termin in der Spalte jedes Beteiligten, und deshalb
 * ist es trotzdem ein Termin: ein Besuch beim Kunden, ein Bericht, und die
 * Arbeitszeit je Mensch getrennt gestempelt.
 *
 * Verschoben wird durch Ziehen, und zwar nur von der Geschaeftsleitung. Nach
 * oben oder unten heisst andere Uhrzeit, und zwar fuer das ganze Team. Zur
 * Seite heisst: dieser eine Mensch wird durch den der Zielspalte ersetzt, die
 * uebrigen bleiben. Auf einen Tag in der Leiste heisst anderer Tag. Die Dauer
 * bleibt dabei immer gleich: wer einen Termin verschiebt, will ihn verschieben
 * und nicht kuerzen.
 *
 * Ein Termin, an dem schon gestempelt wurde, laesst sich weder ziehen noch
 * loeschen. Die Buchung haengt daran.
 */

interface Mitglied {
  id: string;
  name: string;
}

interface Termin {
  id: string;
  /**
   * "termin" ist Arbeit beim Kunden, "pause" die geplante Ruhe dazwischen.
   *
   * Die Pause steht als eigener Block im Brett und nicht als Luecke: eine
   * Luecke sagt nicht, ob dort Pause ist oder ob der Tag schlecht geplant
   * wurde, und auf dem Telefon sieht der Mitarbeiter gar nichts davon.
   */
  art: "termin" | "pause";
  /** Die Besetzung. Nie leer: ohne Mitarbeiter ist es kein Termin. */
  mitglieder: Mitglied[];
  /** Dauer mal Anzahl. Zwei Gaertner zwei Stunden sind vier Arbeitsstunden. */
  personenminuten: number;
  datum: string;
  von: string;
  bis: string;
  objektId: string | null;
  objekt: string | null;
  kunde: string | null;
  /**
   * Die Bereiche dieses Besuchs mit ihrem Anteil an der Dauer, der groesste
   * zuerst. Pool 45 und Hauskontrolle 15 sind ein Termin und nicht zwei.
   */
  bereiche: Bereich[];
  /** Dieselben Bereiche als Zeile: "Pool, Hauskontrolle". Fuer die Kachel. */
  abteilung: string | null;
  /** "Herr Meier", "Familie Stoll". Die Ueberschrift des Termins. */
  ansprache: string | null;
  anschrift: string | null;
  ziel: string | null;
  hatKoordinate: boolean;
  notiz: string | null;
  /**
   * Der Stempelstand des Besuchs.
   *
   * Null heisst, dort war noch niemand. "laeuft" heisst eingestempelt und
   * nicht beendet, "beendet" heisst ausgestempelt. Optional, weil eine Kopie
   * auf dem Geraet von gestern das Feld noch nicht kennt; dann faerbt das
   * Brett wie vorher.
   */
  stempel?: "laeuft" | "beendet" | null;
  /**
   * Die Aenderung fuer diesen Tag. Getrennt von der Notiz, weil sie anders
   * gelesen wird: die Notiz gilt dauerhaft am Objekt, die Aenderung heute.
   */
  aenderung: string | null;
  aenderungAm: string | null;
  aenderungVon: string | null;
  aenderungGelesenAm: string | null;
  aenderungGelesenVon: string | null;
  gestempelt: boolean;
  konflikt: {
    art: "abwesenheit" | "ueberschneidung" | "fachfremd" | "feiertag";
    hinweis: string | null;
  } | null;
}

/**
 * Die geschaetzte Fahrt von einem Termin zum naechsten, je Mitarbeiter.
 *
 * Sie kommt fertig gerechnet aus der API und wird hier nur gezeigt. Zwei
 * Rechnungen fuer dieselbe Fahrt liefen frueher oder spaeter auseinander, und
 * dann waere die Frage, welche der beiden Zahlen den Tag zu voll nennt.
 */
interface Fahrt {
  benutzerId: string;
  datum: string;
  vonTerminId: string;
  nachTerminId: string;
  km: number;
  minuten: number;
  /** Die Zeit zwischen Ende des einen und Beginn des naechsten Termins. */
  luecke: number;
  passt: boolean;
}

/** Ein Abwurf aus der Ablage: eine offene Leistung wird zum Termin. */
interface Anlage {
  objektId: string;
  abteilungId: string;
  datum: string;
  von: string;
  bis: string;
  benutzerIds: string[];
}

/** Was am Termin geaendert werden kann, ohne den Dialog zu oeffnen. */
interface Verschiebung {
  benutzerIds?: string[];
  datum?: string;
  von?: string;
  bis?: string;
}

interface Objekt {
  id: string;
  name: string;
  customer_name: string;
}

interface Person {
  id: string;
  display_name: string;
  /**
   * Ob dieser Mensch eine eigene Spalte bekommt.
   *
   * Optional, weil dieselbe Person auch aus aelteren Antworten und aus der
   * Ablage auf dem Geraet kommen kann. Fehlt der Wert, wird die Spalte
   * gezeigt: lieber eine Spalte zu viel als ein unsichtbarer Termin.
   */
  imDienstplan?: boolean;
  /**
   * Wessen Auto dieser Mensch mitbenutzt. Null: er faehrt selbst.
   *
   * Fuer die Ablage die entscheidende Angabe. Wer eine Leistung zu zweit auf
   * eine Spalte zieht, meint den, der dort steht, und seinen Beifahrer; ein
   * beliebiger zweiter Kollege waere ein zweites Auto und damit eine andere
   * Tour.
   */
  faehrtMitId?: string | null;
}

interface Abteilung {
  id: string;
  name: string;
}

/** Ein Bereich am Termin, mit seinem Anteil an der Dauer in Minuten. */
interface Bereich {
  abteilungId: string;
  abteilung: string;
  minuten: number;
}

/**
 * Schrittweite beim Ziehen. Minutengenau zu treffen ist unmoeglich.
 *
 * Dieselben fuenf Minuten, auf die das Ausrichten rastet. Waere hier eine
 * Viertelstunde, spraenge ein ausgerichteter Termin um 10:25 beim Anfassen auf
 * 10:30, ohne dass jemand ihn verschieben wollte.
 */
const RASTER = 5;
/** Wie hoch eine Minute auf dem Brett ist. Zwoelf Stunden ergeben so 792 Pixel. */
const PX_JE_MINUTE = 1.1;
/** Kuerzer wird kein Block, sonst ist der Name nicht mehr zu lesen. */
const MINDESTHOEHE = 46;
/** Ab hier gilt eine Bewegung als Ziehen und nicht als Klick. */
const ZIEHSCHWELLE = 6;

/**
 * Die Farbe einer Kachel.
 *
 * Eine Regel fuer alle drei Bretter, Telefon, Tag und Woche. Stuende sie
 * dreimal im Quelltext, waere ein beendeter Besuch am Tag gruen und in der
 * Woche noch amber, und niemand wuesste, welche der beiden Ansichten stimmt.
 *
 * Die Reihenfolge ist die Aussage:
 *
 * **Pause zuerst.** Sie ist keine Arbeit und wird nicht gestempelt.
 *
 * **Dann der Stempel.** Lila heisst, dort steht gerade jemand. Gruen heisst,
 * der Besuch ist gefahren und abgeschlossen. Das ist die Frage, die das Buero
 * im Laufe des Tages an das Brett stellt.
 *
 * **Der Konflikt zuletzt.** Ein Urlaub oder eine Ueberschneidung an einem
 * Termin, an dem schon gestempelt wurde, ist Vergangenheit: gefahren ist
 * gefahren. Vorher gewarnt wird weiterhin, nur eben an den Kacheln, an denen
 * die Warnung noch etwas aendern kann. Der Hinweistext bleibt in jedem Fall
 * an der Kachel stehen.
 */
function kachelfarbe(x: Termin): string {
  if (x.art === "pause") return "border-linie/50 bg-white";
  if (x.stempel === "laeuft") return "border-purple-600 bg-purple-50";
  if (x.stempel === "beendet") return "border-green-600 bg-green-50";
  if (x.konflikt?.art === "abwesenheit" || x.konflikt?.art === "ueberschneidung") {
    return "border-red-600 bg-red-50";
  }
  if (x.konflikt) return "border-marke bg-marke/10";
  return "border-marke bg-grund/60";
}

export function DienstplanSeite({ user }: { user: SessionUser }) {
  const { t, i18n } = useTranslation();
  const istAdmin = user.role === "admin";

  const [ansicht, setAnsicht] = useState<"tag" | "woche" | "bedarf" | "vorschlag">("tag");
  const [montag, setMontag] = useState(() => wochenanfang(new Date()));
  const [tag, setTag] = useState(() => heute());
  const [termine, setTermine] = useState<Termin[] | null>(null);
  const [fahrten, setFahrten] = useState<Fahrt[]>([]);
  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [bedarf, setBedarf] = useState<Bedarfszeile[]>([]);
  const [personen, setPersonen] = useState<Person[]>([]);
  const [abteilungen, setAbteilungen] = useState<Abteilung[]>([]);
  const [wer, setWer] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  /** Was das Ausrichten getan hat. Verschwindet beim naechsten Handgriff. */
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialogauftrag | null>(null);
  const [ablageStand, setAblageStand] = useState<string | null>(null);

  const tage = useMemo(
    () => Array.from({ length: 7 }, (_, i) => plusTage(montag, i)),
    [montag]
  );

  /*
   * Der gewaehlte Tag muss in der gezeigten Woche liegen. Sonst blaettert
   * jemand eine Woche weiter und sieht weiter den Tag von vorher, ohne zu
   * verstehen, warum sich nichts aendert.
   */
  useEffect(() => {
    if (!tage.includes(tag)) setTag(tage[0]);
  }, [tage, tag]);

  const laden = useCallback(async () => {
    const p = new URLSearchParams({ von: montag, bis: plusTage(montag, 6) });
    if (istAdmin && wer) p.set("benutzerId", wer);
    /*
     * Die eigene Woche liegt als Kopie auf dem Geraet. Wer morgens ohne Netz
     * losfaehrt, muss trotzdem wissen, wo er hinsoll. Die Kopie traegt ihr
     * Datum, damit niemand einen Plan von vorgestern fuer den heutigen haelt.
     */
    const { daten: a, stand } = await holenMitAblage<{ termine: Termin[]; fahrten?: Fahrt[] }>(
      `/termine?${p.toString()}`
    );
    setAblageStand(stand);
    setTermine(a.termine);
    /*
     * Eine Kopie von gestern kennt die Fahrten noch nicht. Dann bleibt die
     * Liste leer, statt dass die Ansicht abstuerzt: der Plan ist wichtiger
     * als die Fahrzeit.
     */
    setFahrten(a.fahrten ?? []);

    /*
     * Der Bedarf wird bei jedem Laden mitgeholt, denn er ist die Ablage.
     *
     * Was dort steht, ist Soll minus Geplant. Bliebe die Liste stehen, waehrend
     * das Brett sich fuellt, zoege man dieselbe Leistung ein zweites Mal auf
     * einen anderen Tag. Ein eigener Ladepfad waere ein zweiter Zeitpunkt, an
     * dem die beiden auseinanderlaufen koennen.
     *
     * Ohne Netz faellt sie leer aus. Die Ablage verschwindet dann, das Brett
     * bleibt: planen kann man nur online, ablesen auch offline.
     */
    if (!istAdmin) return;
    await api.get<{ zeilen: Bedarfszeile[] }>(`/bedarf?von=${montag}`)
      .then((b) => setBedarf(b.zeilen))
      .catch(() => setBedarf([]));
  }, [montag, istAdmin, wer]);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  useEffect(() => {
    void api.get<{ abteilungen: Abteilung[] }>("/abteilungen")
      .then((a) => setAbteilungen(a.abteilungen)).catch(() => setAbteilungen([]));
  }, []);

  useEffect(() => {
    if (!istAdmin) return;
    void api.get<{ properties: Objekt[] }>("/properties")
      .then((a) => setObjekte(a.properties)).catch(() => setObjekte([]));
    void api.get<{ users: Person[] }>("/auth/users")
      .then((a) => setPersonen(a.users)).catch(() => setPersonen([]));
  }, [istAdmin]);

  /**
   * Die Spalten des Tagesbretts.
   *
   * Fuer die Geschaeftsleitung alle Mitarbeiter, oder genau der eine, nach dem
   * gefiltert wird. Ein Mitarbeiter sieht ausschliesslich sich selbst: die
   * Liste der Kollegen bekommt er von der API gar nicht erst.
   */
  const spalten: Person[] = useMemo(() => {
    if (!istAdmin) return [{ id: user.id, display_name: user.displayName }];
    const alle = personen.length > 0
      ? personen
      : [{ id: user.id, display_name: user.displayName }];
    if (wer) return alle.filter((p) => p.id === wer);
    /*
     * Ausgeblendete Spalten bleiben weg, solange nichts darin steht. Wer trotz
     * Ausblendung in dieser Woche einen Termin hat, bekommt seine Spalte
     * zurueck: ein Termin, den niemand sieht, ist schlimmer als eine Spalte
     * zu viel.
     */
    const belegt = new Set(
      (termine ?? []).flatMap((x) => x.mitglieder.map((m) => m.id))
    );
    return alle.filter((p) => p.imDienstplan !== false || belegt.has(p.id));
  }, [istAdmin, personen, wer, termine, user.id, user.displayName]);

  const desTages = useMemo(
    () => (termine ?? []).filter((x) => x.datum === tag),
    [termine, tag]
  );

  /** Der eine gewaehlte Mensch, oder nichts, wenn alle gezeigt werden. */
  const gewaehlte = useMemo(
    () => (wer ? spalten.find((p) => p.id === wer) ?? null : null),
    [wer, spalten]
  );

  /**
   * Verschieben.
   *
   * Die Anzeige geht sofort mit, der Server wird gefragt und danach neu
   * geladen. Ohne den Vorgriff haengt der Block eine halbe Sekunde an der
   * alten Stelle, und das sieht aus, als haette das Ziehen nicht geklappt.
   * Geht es schief, steht der Grund im Klartext da und die Liste ist nach dem
   * Neuladen wieder die des Servers.
   */
  /**
   * Einen Tag ausrichten.
   *
   * Das Buero traegt die Termine hintereinander ein, ohne Abstand. Der Knopf
   * macht daraus einen Tag, den man auch fahren kann: jeder Termin rueckt um
   * die geschaetzte Fahrt nach hinten, gerastet auf eine Viertelstunde, und
   * die Pause bekommt ihren Platz zwischen zwei Terminen.
   *
   * Was danach nicht mehr in den Feierabend passt, bleibt trotzdem stehen und
   * wird gemeldet. Ein Termin, der sich von selbst auf einen anderen Tag
   * verschiebt, kostet einen Kundenanruf.
   */
  async function ausrichten(benutzerId: string, optimieren = false, datum = tag) {
    setFehler(null);
    setHinweis(null);
    try {
      const a = await api.post<{
        verschoben: number;
        pause: { von: string; bis: string } | null;
        pauseImTermin: string | null;
        ueberzug: number;
        route: { vorher: number; nachher: number; fest: number; genau: boolean } | null;
      }>(optimieren ? "/termine/route-optimieren" : "/termine/tag-ausrichten",
        { benutzerId, datum });
      await laden();
      const teile: string[] = [];
      /*
       * Beim Optimieren steht die gesparte Fahrzeit zuerst. Sie ist die
       * Antwort auf die Frage, warum jemand den Knopf gedrueckt hat; wie viele
       * Termine sich dabei verschoben haben, ist Beiwerk.
       */
      if (a.route) {
        teile.push(a.route.nachher < a.route.vorher
          ? t("plan.routeKuerzer", { count: a.route.vorher - a.route.nachher })
          : t("plan.routeSchonKurz"));
        if (a.route.fest > 0) teile.push(t("plan.routeFest", { count: a.route.fest }));
      }
      teile.push(t("plan.ausgerichtet", { count: a.verschoben }));
      if (a.pause) teile.push(t("plan.pauseGesetzt", { von: a.pause.von, bis: a.pause.bis }));
      if (a.pauseImTermin) teile.push(t("plan.pauseImTermin"));
      if (a.ueberzug > 0) teile.push(t("plan.ueberzug", { count: a.ueberzug }));
      setHinweis(teile.join(" · "));
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function verschieben(id: string, aenderung: Verschiebung) {
    setFehler(null);
    setTermine((alt) => (alt ?? []).map((x) => {
      if (x.id !== id) return x;
      const mitglieder = aenderung.benutzerIds
        ? aenderung.benutzerIds.map(
            (b) => x.mitglieder.find((m) => m.id === b)
              ?? { id: b, name: personen.find((p) => p.id === b)?.display_name ?? "" }
          )
        : x.mitglieder;
      return {
        ...x,
        mitglieder,
        datum: aenderung.datum ?? x.datum,
        von: aenderung.von ?? x.von,
        bis: aenderung.bis ?? x.bis
      };
    }));
    try {
      await api.patch(`/termine/${id}`, aenderung);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      await laden().catch(() => undefined);
    }
  }

  /**
   * Eine Leistung aus der Ablage wird zum Termin.
   *
   * Zwei Faelle. Steht am selben Objekt zur selben Zeit schon ein Termin mit
   * genau dieser Besetzung, waechst er um den neuen Bereich, statt dass ein
   * zweiter danebengelegt wird. Pool 45 und Hauskontrolle 15 sind ein Besuch,
   * und zwei Kacheln uebereinander sind ausserdem eine Ueberschneidung, die
   * das Brett rot faerbt, obwohl niemand doppelt arbeitet.
   *
   * Sonst entsteht ein neuer Termin. Die Besetzung kommt vom Abwurf mit, denn
   * nur sie sagt, ob die offenen Personenminuten einen Tag oder einen halben
   * fuellen.
   */
  async function anlegen(a: Anlage) {
    setFehler(null);
    setHinweis(null);
    const von = minuten(a.von);
    const bis = minuten(a.bis);
    const gleich = (x: Termin) =>
      x.mitglieder.length === a.benutzerIds.length
      && a.benutzerIds.every((b) => x.mitglieder.some((m) => m.id === b));
    const treffer = (termine ?? []).find((x) =>
      x.art === "termin" && x.datum === a.datum && !x.gestempelt
      && x.objektId === a.objektId && gleich(x)
      && minuten(x.von) <= bis && minuten(x.bis) >= von
      && !x.bereiche.some((b) => b.abteilungId === a.abteilungId)
    );
    try {
      if (treffer) {
        const laenger = alsUhrzeit(minuten(treffer.bis) + (bis - von));
        await api.patch(`/termine/${treffer.id}`, {
          bis: laenger,
          bereiche: [
            ...treffer.bereiche.map((b) => ({ abteilungId: b.abteilungId, minuten: b.minuten })),
            { abteilungId: a.abteilungId, minuten: bis - von }
          ]
        });
        setHinweis(t("plan.ablageErgaenzt", { objekt: treffer.ansprache ?? treffer.objekt ?? "" }));
      } else {
        await api.post("/termine", {
          benutzerIds: a.benutzerIds,
          objektId: a.objektId,
          datum: a.datum,
          von: a.von,
          bis: a.bis,
          bereiche: [{ abteilungId: a.abteilungId, minuten: bis - von }]
        });
      }
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      await laden().catch(() => undefined);
    }
  }

  async function loeschen(id: string) {
    setFehler(null);
    try {
      await api.del(`/termine/${id}`);
      await laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <>
      <Seitenkopf
        titel={istAdmin ? t("plan.titel") : t("nav.meineWoche")}
        hinweis={istAdmin ? t("plan.hinweis") : undefined}
        aktionen={
          <div className="w-full sm:w-auto">
            <p className="mb-2 text-sm font-semibold tabular-nums sm:hidden">
              {kurzdatum(montag, i18n.language)} bis {kurzdatum(plusTage(montag, 6), i18n.language)}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Umschalter
                ansicht={ansicht}
                onWechsel={setAnsicht}
                istAdmin={istAdmin}
                beschriftung={{
                  tag: t("plan.ansichtTag"),
                  woche: t("plan.ansichtWoche"),
                  bedarf: t("plan.ansichtBedarf"),
                  vorschlag: t("plan.ansichtVorschlag")
                }}
              />
              <Knopf art="neben" onClick={() => setMontag(plusTage(montag, -7))}>
                {t("plan.zurueck")}
              </Knopf>
              <span className="hidden text-sm font-semibold tabular-nums sm:inline">
                {kurzdatum(montag, i18n.language)} bis {kurzdatum(plusTage(montag, 6), i18n.language)}
              </span>
              <Knopf art="neben" onClick={() => setMontag(plusTage(montag, 7))}>
                {t("plan.vor")}
              </Knopf>
              <Knopf
                art="neben"
                onClick={() => { setMontag(wochenanfang(new Date())); setTag(heute()); }}
              >
                {t("plan.heute")}
              </Knopf>
            </div>
          </div>
        }
      />
      <Fehler text={fehler} />
      {hinweis && (
        <p className="mb-4 rounded border border-marke/40 bg-marke/10 px-3 py-2 text-sm">
          {hinweis}
        </p>
      )}
      <AblageHinweis stand={ablageStand} />

      {istAdmin && personen.length > 0 && ansicht !== "bedarf" && (
        <div className="mb-4">
          <Feld label={t("zeit.mitarbeiter")}>
            <Auswahl value={wer} onChange={(e) => setWer(e.target.value)}>
              <option value="">{t("zeit.alle")}</option>
              {personen.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </Auswahl>
          </Feld>
        </div>
      )}

      {ansicht === "bedarf" ? (
        <Bedarfsliste montag={montag} />
      ) : ansicht === "vorschlag" ? (
        <Vorschlagsliste
          montag={montag}
          nurFuer={wer}
          name={personen.find((p) => p.id === wer)?.display_name ?? null}
          onUebernommen={() => { setAnsicht("tag"); void laden(); }}
        />
      ) : !termine ? <Laedt /> : ansicht === "woche" ? (
        /*
         * Mit einem gewaehlten Mitarbeiter wird aus der Zusammenfassung ein
         * Brett: fuenf Tagesspalten fuer diesen einen Menschen. Ohne Auswahl
         * bleibt es bei den Zahlen, denn vier Leute mal fuenf Tage auf einem
         * Bildschirm ergaeben ein Raster, in dem niemand mehr etwas findet.
         */
        gewaehlte ? (
          <Wochenbrett
            tage={tage}
            termine={termine}
            fahrten={fahrten}
            person={gewaehlte}
            istAdmin={istAdmin}
            onTag={(x) => { setTag(x); setAnsicht("tag"); }}
            onOeffnen={(x) => setDialog({ art: "aendern", termin: x })}
            onNeu={(datum, von) =>
              setDialog({ art: "neu", tag: datum, benutzerId: gewaehlte.id, von })}
            onAusrichten={(datum, optimieren) =>
              void ausrichten(gewaehlte.id, optimieren, datum)}
          />
        ) : (
          <Wochenuebersicht
            tage={tage}
            termine={termine}
            onTag={(x) => { setTag(x); setAnsicht("tag"); }}
          />
        )
      ) : (
        <Tagesansicht
          tag={tag}
          tage={tage}
          termine={desTages}
          alleTermine={termine}
          fahrten={fahrten}
          spalten={spalten}
          alle={personen.length > 0 ? personen : spalten}
          bedarf={bedarf}
          istAdmin={istAdmin}
          onTag={setTag}
          onVerschieben={verschieben}
          onOeffnen={(x) => setDialog({ art: "aendern", termin: x })}
          onNeu={(benutzerId, von) =>
            setDialog({ art: "neu", tag, benutzerId, von })}
          onAusrichten={(benutzerId, optimieren) => void ausrichten(benutzerId, optimieren)}
          onAnlegen={(a) => void anlegen(a)}
        />
      )}

      {dialog && (
        <TerminDialog
          auftrag={dialog}
          /*
           * Zur Auswahl stehen alle, nicht nur die Spalten des Brettes.
           *
           * Sonst waere das Ausblenden im Team eine Sperre: wer keine Spalte
           * hat, liesse sich auch nicht mehr eintragen. Genau das soll es
           * nicht sein. Die Geschaeftsleitung faehrt selten raus, aber wenn,
           * dann muss sie sich eintragen koennen, und ihre Spalte kommt an dem
           * Tag von selbst zurueck.
           */
          personen={personen.length > 0 ? personen : spalten}
          objekte={objekte}
          abteilungen={abteilungen}
          onClose={() => setDialog(null)}
          onFertig={() => { setDialog(null); void laden(); }}
          onLoeschen={async (id) => { setDialog(null); await loeschen(id); }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Umschalter
// ---------------------------------------------------------------------

type Ansicht = "tag" | "woche" | "bedarf" | "vorschlag";

function Umschalter({
  ansicht, onWechsel, istAdmin, beschriftung
}: {
  ansicht: Ansicht;
  onWechsel: (x: Ansicht) => void;
  istAdmin: boolean;
  beschriftung: { tag: string; woche: string; bedarf: string; vorschlag: string };
}) {
  const stil = (aktiv: boolean) =>
    `min-h-11 px-4 text-sm font-semibold transition ${
      aktiv ? "bg-marke text-white" : "bg-white text-tinte hover:bg-grund"
    }`;
  return (
    <div className="inline-flex overflow-hidden rounded border border-linie/40">
      <button className={stil(ansicht === "tag")} onClick={() => onWechsel("tag")}>
        {beschriftung.tag}
      </button>
      <button className={stil(ansicht === "woche")} onClick={() => onWechsel("woche")}>
        {beschriftung.woche}
      </button>
      {/* Der Bedarf ist die Frage der Geschaeftsleitung, nicht die des
          Mitarbeiters: er zeigt die Sollstunden aller Kunden. */}
      {istAdmin && (
        <>
          <button className={stil(ansicht === "bedarf")} onClick={() => onWechsel("bedarf")}>
            {beschriftung.bedarf}
          </button>
          <button
            className={stil(ansicht === "vorschlag")}
            onClick={() => onWechsel("vorschlag")}
          >
            {beschriftung.vorschlag}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Woche als Zusammenfassung
// ---------------------------------------------------------------------

/**
 * Die Woche zeigt Zahlen, keine Termine.
 *
 * Vierundzwanzig Karten je Tag ergeben eine Seite, durch die niemand scrollt.
 * Was man in der Wochenansicht wirklich sucht, sind zwei Dinge: an welchem Tag
 * ist viel los, und wer ist an dem Tag im Einsatz. Beides steht hier in je
 * einer Zeile.
 */
function Wochenuebersicht({
  tage, termine, onTag
}: { tage: string[]; termine: Termin[]; onTag: (tag: string) => void }) {
  const { t, i18n } = useTranslation();

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {tage.map((tag) => {
        const desTages = termine.filter((x) => x.datum === tag);
        /*
         * Ein Termin mit zwei Menschen steht in beiden Zeilen. Das ist keine
         * Doppelzaehlung, sondern die Antwort auf die Frage, wer an dem Tag
         * unterwegs ist.
         */
        const jeMensch = new Map<string, Termin[]>();
        for (const x of desTages) {
          for (const m of x.mitglieder) {
            const liste = jeMensch.get(m.name) ?? [];
            liste.push(x);
            jeMensch.set(m.name, liste);
          }
        }
        const konflikte = desTages.filter((x) => x.konflikt).length;

        return (
          <button
            key={tag}
            onClick={() => onTag(tag)}
            className="rounded-card bg-white p-4 text-start shadow-sm transition hover:bg-grund"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className={`font-bold ${tag === heute() ? "text-verweis" : ""}`}>
                {wochentag(tag, i18n.language)}
              </span>
              <span className="text-xs text-leise tabular-nums">
                {kurzdatum(tag, i18n.language)}
              </span>
            </div>

            {desTages.length === 0 ? (
              <p className="mt-2 text-sm text-leise">{t("plan.frei")}</p>
            ) : (
              <>
                <p className="mt-2 text-sm font-semibold">
                  {t("plan.termineZahl", { count: desTages.length })}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {[...jeMensch.entries()].map(([name, liste]) => (
                    <li key={name} className="text-xs text-leise">
                      {name}: {liste.length} · {spanne(liste)}
                    </li>
                  ))}
                </ul>
                {konflikte > 0 && (
                  <p className="mt-2 text-xs font-semibold text-red-700">
                    {t("plan.konflikteZahl", { count: konflikte })}
                  </p>
                )}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function spanne(liste: Termin[]): string {
  const von = liste.map((x) => x.von).sort()[0];
  const bis = liste.map((x) => x.bis).sort().slice(-1)[0];
  return `${von} bis ${bis}`;
}

// ---------------------------------------------------------------------
// Was die Woche noch braucht
// ---------------------------------------------------------------------

interface Bedarfszeile {
  objektId: string;
  objekt: string;
  kunde: string | null;
  abteilungId: string;
  abteilung: string;
  soll: number;
  geplant: number;
  offen: number;
}

/**
 * Soll gegen Plan, je Objekt und Abteilung.
 *
 * Eine Sollzeit am Objekt, die niemand mit dem Kalender vergleicht, ist eine
 * Zahl in einem Formular. Erst diese Liste beantwortet die Frage, mit der
 * jeder Montag anfaengt: wo fehlt diese Woche noch etwas.
 *
 * Gerechnet wird auf beiden Seiten in Personenstunden. Zwei Gaertner zwei
 * Stunden sind vier geleistete Stunden.
 */
function Bedarfsliste({ montag }: { montag: string }) {
  const { t } = useTranslation();
  const [zeilen, setZeilen] = useState<Bedarfszeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setZeilen(null);
    void api.get<{ zeilen: Bedarfszeile[] }>(`/bedarf?von=${montag}`)
      .then((a) => setZeilen(a.zeilen))
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [montag, t]);

  if (fehler) return <Fehler text={fehler} />;
  if (!zeilen) return <Laedt />;
  if (zeilen.length === 0) {
    return <Karte><p className="text-sm text-leise">{t("plan.bedarfLeer")}</p></Karte>;
  }

  const offen = zeilen.filter((z) => z.offen > 0);

  return (
    <>
      <p className="mb-3 text-sm text-leise">
        {t("plan.bedarfOffen", { count: offen.length })}
      </p>
      <div className="overflow-x-auto rounded-card bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-linie text-start text-xs text-leise">
            <tr>
              <th className="p-3 text-start">{t("plan.bedarfObjekt")}</th>
              <th className="p-3 text-start">{t("plan.abteilung")}</th>
              <th className="p-3 text-end">{t("plan.bedarfSoll")}</th>
              <th className="p-3 text-end">{t("plan.bedarfGeplant")}</th>
              <th className="p-3 text-end">{t("plan.bedarfRest")}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={`${z.objektId}-${z.abteilungId}`} className="border-b border-linie/60">
                <td className="p-3">
                  <span className="font-semibold">{z.objekt}</span>
                  {z.kunde && <span className="block text-xs text-leise">{z.kunde}</span>}
                </td>
                <td className="p-3">{z.abteilung}</td>
                <td className="p-3 text-end tabular-nums">{alsStunden(z.soll)}</td>
                <td className="p-3 text-end tabular-nums">{alsStunden(z.geplant)}</td>
                <td
                  className={`p-3 text-end font-semibold tabular-nums ${
                    z.offen > 0 ? "text-red-700" : z.offen < 0 ? "text-verweis" : "text-leise"
                  }`}
                >
                  {/*
                    * Minus heisst fehlt, Plus heisst mehr geplant als
                    * vereinbart. Ein rotes "-15,5 h" liest sich beim
                    * Ueberfliegen wie ein Rueckstand, obwohl es das Gegenteil
                    * ist, deshalb steht das Vorzeichen hier ausdruecklich da.
                    */}
                  {z.offen === 0
                    ? "—"
                    : z.offen > 0
                      ? alsStunden(z.offen)
                      : `+${alsStunden(-z.offen)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// Wochenvorschlag
// ---------------------------------------------------------------------

interface Vorschlagszeile {
  objektId: string;
  objekt: string;
  kunde: string | null;
  gemeinde: string | null;
  /** Die Bereiche dieses Besuchs, der groesste Anteil zuerst. */
  bereiche: Bereich[];
  datum: string;
  von: string;
  bis: string;
  benutzerIds: string[];
  benutzer: string[];
  personenminuten: number;
  /** Geschaetzte Anfahrt vom vorherigen Halt. Null beim ersten Termin des Tages. */
  anfahrt: number | null;
  /** Der gewohnte Wochentag dieses Objekts, aus den letzten acht Wochen. */
  gewohnterTag: number | null;
  /** Der Termin liegt nicht am gewohnten Tag. */
  abweichendVomGewohnten: boolean;
}

interface Vorschlagshinweis {
  objekt: string;
  abteilung: string;
  offen: number;
  grund: "keine-fachkraft" | "kein-platz" | "ohne-koordinate" | "festtermin-belegt"
    | "nur-mitfahrt";
}

/**
 * Der Grund als Text.
 *
 * Als feste Zuordnung und nicht als zusammengesetzter Schluessel, denn ein
 * Schluessel, den erst die Laufzeit bildet, faellt bei der Pruefung der
 * Uebersetzungen durch das Raster und steht spaeter roh in der Oberflaeche.
 */
const GRUND: Record<Vorschlagshinweis["grund"], string> = {
  "keine-fachkraft": "plan.grundKeineFachkraft",
  "kein-platz": "plan.grundKeinPlatz",
  "ohne-koordinate": "plan.grundOhneKoordinate",
  "festtermin-belegt": "plan.grundFesttermin",
  "nur-mitfahrt": "plan.grundNurMitfahrt"
};

/**
 * Der Vorschlag fuer die Woche.
 *
 * Er steht als Entwurf da und nicht im Kalender. Uebernommen wird zeilenweise
 * oder alles auf einmal, und jede uebernommene Zeile laeuft durch dieselbe
 * Anlage wie ein von Hand gesetzter Termin. Ein Knopf, der die Woche
 * ungefragt buchen wuerde, waere beim ersten falschen Termin abgeschaltet, und
 * danach plante wieder jemand auf Papier.
 *
 * Was nicht untergebracht werden konnte, steht darunter mit Grund. Ein
 * Vorschlag, der die Haelfte verschweigt, sieht vollstaendig aus und ist
 * deshalb schlimmer als gar keiner.
 */
function Vorschlagsliste({
  montag, nurFuer, name, onUebernommen
}: {
  montag: string;
  /** Leer heisst: fuer alle rechnen. Sonst nur fuer diesen Menschen. */
  nurFuer: string;
  name: string | null;
  onUebernommen: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [zeilen, setZeilen] = useState<Vorschlagszeile[] | null>(null);
  const [hinweise, setHinweise] = useState<Vorschlagshinweis[]>([]);
  const [verworfen, setVerworfen] = useState<Set<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setZeilen(null);
    setVerworfen(new Set());
    void api.get<{ vorschlaege: Vorschlagszeile[]; hinweise: Vorschlagshinweis[] }>(
      `/vorschlag?von=${montag}${nurFuer ? `&benutzerId=${nurFuer}` : ""}`
    )
      .then((a) => { setZeilen(a.vorschlaege); setHinweise(a.hinweise); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [montag, nurFuer, t]);

  const schluessel = (z: Vorschlagszeile) =>
    `${z.datum}-${z.von}-${z.objektId}-${z.bereiche.map((b) => b.abteilungId).join("+")}`;

  async function uebernehmen(auswahl: Vorschlagszeile[]) {
    if (auswahl.length === 0) return;
    setLaeuft(true);
    setFehler(null);
    try {
      for (const z of auswahl) {
        await api.post("/termine", {
          benutzerIds: z.benutzerIds,
          datum: z.datum,
          von: z.von,
          bis: z.bis,
          objektId: z.objektId,
          bereiche: z.bereiche.map((b) => ({
            abteilungId: b.abteilungId, minuten: b.minuten
          }))
        });
      }
      onUebernommen();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  if (fehler && !zeilen) return <Fehler text={fehler} />;
  if (!zeilen) return <Laedt />;

  const offen = zeilen.filter((z) => !verworfen.has(schluessel(z)));
  const tage = [...new Set(offen.map((z) => z.datum))].sort();

  return (
    <>
      <Fehler text={fehler} />
      <Karte className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-leise">{t("plan.vorschlagHinweis")}</p>
            {/*
              * Wer den Entwurf fuer einen Einzelnen rechnen laesst, muss das
              * am Ergebnis sehen. Sonst sieht ein Plan, in dem die halbe
              * Mannschaft fehlt, aus wie ein Plan mit einem Fehler.
              */}
            {name && (
              <p className="mt-1 text-sm font-semibold">
                {t("plan.vorschlagNurFuer", { name })}
              </p>
            )}
          </div>
          <Knopf
            onClick={() => void uebernehmen(offen)}
            disabled={laeuft || offen.length === 0}
          >
            {t("plan.vorschlagAlle", { count: offen.length })}
          </Knopf>
        </div>
      </Karte>

      {offen.length === 0 ? (
        <Karte><p className="text-sm text-leise">{t("plan.vorschlagLeer")}</p></Karte>
      ) : (
        <div className="space-y-4">
          {tage.map((tag) => (
            <section key={tag}>
              <h2 className="mb-2 text-sm font-bold">
                {wochentag(tag, i18n.language)} {kurzdatum(tag, i18n.language)}
              </h2>
              <ul className="space-y-2">
                {offen.filter((z) => z.datum === tag).map((z) => (
                  <li
                    key={schluessel(z)}
                    className="flex flex-wrap items-center gap-3 rounded-card border-s-4 border-dashed border-marke bg-white p-3 shadow-sm"
                  >
                    <span className="text-sm font-semibold tabular-nums">
                      {z.von} bis {z.bis}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-bold">{z.kunde ?? z.objekt}</span>
                      <span className="block truncate text-xs text-leise">
                        {z.objekt}
                        {z.gemeinde ? `, ${z.gemeinde}` : ""}
                        {/*
                          Mehrere Bereiche mit ihren Anteilen: "Pool 45 Min,
                          Hauskontrolle 15 Min". Bei einem einzigen steht die
                          Zahl nicht dabei, sie waere dann die Dauer und die
                          steht schon links.
                        */}
                        {z.bereiche.length > 0 && " · "}
                        {z.bereiche
                          .map((b) => z.bereiche.length > 1
                            ? `${b.abteilung} ${t("plan.bereichMinuten", { count: b.minuten })}`
                            : b.abteilung)
                          .join(", ")}
                      </span>
                      <span className="block truncate text-xs text-leise">
                        {z.benutzer.join(", ")} · {alsStunden(z.personenminuten)}
                        {/* Null heisst Tagesbeginn, Null Minuten heisst
                            dasselbe Objekt. Beides ist keine Fahrt. */}
                        {z.anfahrt
                          ? ` · ${t("plan.anfahrtMinuten", { count: z.anfahrt })}`
                          : ""}
                      </span>
                      {/*
                        Woran der Vorschlag sich orientiert hat. Ohne diese
                        Zeile folgte er still einer Gewohnheit, die nirgends
                        aufgeschrieben steht, und das waere nicht zu erklaeren,
                        sobald er einmal etwas Unerwartetes tut.
                      */}
                      {z.gewohnterTag !== null && (
                        <span className={`block truncate text-xs ${
                          z.abweichendVomGewohnten ? "font-semibold text-verweis" : "text-leise"
                        }`}>
                          {z.abweichendVomGewohnten
                            ? t("plan.abweichendVomGewohnten", { tag: t(`wochentag.${z.gewohnterTag}`) })
                            : t("plan.wieGewohnt", { tag: t(`wochentag.${z.gewohnterTag}`) })}
                        </span>
                      )}
                    </span>
                    <Textknopf
                      onClick={() => setVerworfen(new Set([...verworfen, schluessel(z)]))}
                    >
                      {t("plan.vorschlagVerwerfen")}
                    </Textknopf>
                    <Knopf art="neben" onClick={() => void uebernehmen([z])} disabled={laeuft}>
                      {t("plan.vorschlagUebernehmen")}
                    </Knopf>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {hinweise.length > 0 && (
        <Karte className="mt-4">
          <h2 className="mb-2 text-sm font-bold">{t("plan.vorschlagOffen")}</h2>
          <ul className="space-y-1 text-sm">
            {hinweise.map((h, i) => (
              <li key={`${h.objekt}-${h.abteilung}-${h.grund}-${i}`}>
                <span className="font-semibold">{h.objekt}</span>
                {" · "}{h.abteilung}{" · "}
                <span className="tabular-nums">{alsStunden(h.offen)}</span>
                {" · "}
                <span className="text-leise">{t(GRUND[h.grund])}</span>
              </li>
            ))}
          </ul>
        </Karte>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Der Tag
// ---------------------------------------------------------------------

/**
 * Eine offene Leistung, die noch keinen Termin hat.
 *
 * Sie liegt in der Ablage rechts neben den Spalten und wird von dort auf einen
 * Menschen und eine Uhrzeit gezogen. Erst der Abwurf macht daraus einen
 * Termin; bis dahin ist sie nur eine Zeile aus dem Bedarf.
 */
interface Vorlage {
  objektId: string;
  abteilungId: string;
  titel: string;
  abteilung: string;
  /** Offene Personenminuten. Durch die Teamgroesse geteilt ergibt sich die Dauer. */
  offen: number;
  /** Wie viele hinfahren. Bestimmt die Dauer und die Besetzung beim Abwurf. */
  personen: number;
}

interface Zug {
  /** Der gezogene Termin, oder null: dann kommt der Block aus der Ablage. */
  id: string | null;
  /** Die gezogene Leistung, oder null: dann ist es ein bestehender Termin. */
  vorlage: Vorlage | null;
  /** Aus wessen Spalte der Block gezogen wurde. Er wird ersetzt, nicht das Team. */
  quelleId: string;
  dauer: number;
  breite: number;
  hoehe: number;
  griffX: number;
  griffY: number;
  x: number;
  y: number;
  gezogen: boolean;
  titel: string;
  zeit: string | null;
}

/**
 * Die Woche eines einzelnen Menschen als Brett.
 *
 * Die Wochenuebersicht zeigt Zahlen, und das ist richtig, solange man auf
 * alle vier Spalten schaut. Sobald man aber die Woche eines Einzelnen baut,
 * will man dieselbe Ansicht wie am Tag, nur mit den Tagen als Spalten: erst
 * Max Mustermanns Pools ueber die Woche verteilen, dann Anas Gaerten um das
 * herum, was schon steht.
 *
 * Bewusst ohne Ziehen. Am Tagesbrett verschiebt ein Zug einen Termin zwischen
 * Menschen und Uhrzeiten, und dieselbe Geste haette hier eine andere
 * Bedeutung, naemlich einen anderen Tag. Zwei Gesten, die gleich aussehen und
 * verschieden wirken, sind schlimmer als eine fehlende. Angeklickt wird
 * weiterhin alles.
 */
function Wochenbrett({
  tage, termine, fahrten, person, istAdmin, onTag, onOeffnen, onNeu, onAusrichten
}: {
  tage: string[];
  termine: Termin[];
  fahrten: Fahrt[];
  person: Person;
  istAdmin: boolean;
  onTag: (tag: string) => void;
  onOeffnen: (x: Termin) => void;
  onNeu: (tag: string, von: string) => void;
  onAusrichten: (tag: string, optimieren: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [beginn, ende] = useMemo(() => zeitraum(termine), [termine]);
  const hoehe = (ende - beginn) * PX_JE_MINUTE;

  const stundenmarken: number[] = [];
  for (let m = beginn; m <= ende; m += 60) stundenmarken.push(m);

  return (
    <div className="flex">
      <div className="w-14 shrink-0">
        <div className="h-10" />
        <div className="relative" style={{ height: hoehe }}>
          {stundenmarken.map((m) => (
            <div
              key={m}
              className="absolute -translate-y-1/2 text-xs text-leise tabular-nums"
              style={{ top: (m - beginn) * PX_JE_MINUTE }}
            >
              {alsUhrzeit(m)}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-1 gap-2 overflow-x-auto">
        {tage.map((tag) => {
          const seine = termine
            .filter((x) => x.datum === tag)
            .sort((a, b) => a.von.localeCompare(b.von));
          const spuren = bahnen(seine);
          const desTages = fahrten.filter((ffi) => ffi.datum === tag);
          return (
            <div key={tag} className="min-w-[7.5rem] flex-1">
              <div className="flex h-10 flex-col">
                <div className="flex items-baseline justify-between gap-1">
                  <button
                    className="truncate text-sm font-bold underline decoration-transparent hover:decoration-inherit"
                    onClick={() => onTag(tag)}
                  >
                    {wochentag(tag, i18n.language)} {kurzdatum(tag, i18n.language)}
                  </button>
                  <span className="shrink-0 text-xs text-leise">{seine.length}</span>
                </div>
                {/*
                  * Kurze Beschriftungen, weil eine Tagesspalte schmal ist.
                  * "Tag ausrichten" bricht dort um oder wird abgeschnitten,
                  * und ein halbes Wort auf einem Knopf sieht nach Fehler aus.
                  */}
                <div className="flex items-baseline gap-2">
                  {istAdmin && seine.length > 1 && (
                    <Textknopf onClick={() => onAusrichten(tag, false)}>
                      {t("plan.ausrichtenKurz")}
                    </Textknopf>
                  )}
                  {istAdmin && seine.length > 2 && (
                    <Textknopf onClick={() => onAusrichten(tag, true)}>
                      {t("plan.routeKurz")}
                    </Textknopf>
                  )}
                </div>
              </div>

              <div
                className="relative rounded border border-linie bg-white"
                style={{ height: hoehe }}
                onClick={(e) => {
                  if (!istAdmin) return;
                  if ((e.target as HTMLElement).closest("[data-termin]")) return;
                  const kasten = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const minute = begrenzen(
                    Math.round((beginn + (e.clientY - kasten.top) / PX_JE_MINUTE) / RASTER) * RASTER,
                    beginn,
                    ende - 60
                  );
                  onNeu(tag, alsUhrzeit(minute));
                }}
              >
                {stundenmarken.map((m) => (
                  <div
                    key={m}
                    className="pointer-events-none absolute inset-x-0 border-t border-linie"
                    style={{ top: (m - beginn) * PX_JE_MINUTE }}
                  />
                ))}

                {desTages.map((ffi) => {
                  const vorher = seine.find((x) => x.id === ffi.vonTerminId);
                  const nachher = seine.find((x) => x.id === ffi.nachTerminId);
                  if (!vorher || !nachher) return null;
                  const start = minuten(vorher.bis);
                  const streifen = Math.max(14, (minuten(nachher.von) - start) * PX_JE_MINUTE);
                  return (
                    <div
                      key={`${ffi.vonTerminId}-${ffi.nachTerminId}`}
                      title={t("plan.fahrtTitel", { km: ffi.km, minuten: ffi.minuten })}
                      className={`pointer-events-none absolute inset-x-0 flex items-center justify-center overflow-hidden rounded text-[10px] font-semibold ${
                        ffi.passt ? "bg-grund/70 text-leise" : "bg-red-100 text-red-700"
                      }`}
                      style={{ top: (start - beginn) * PX_JE_MINUTE, height: streifen }}
                    >
                      <span className="truncate px-1 tabular-nums">
                        {t("plan.fahrtKurz", { minuten: ffi.minuten })}
                      </span>
                    </div>
                  );
                })}

                {seine.map((x) => {
                  const von = minuten(x.von);
                  const dauer = Math.max(RASTER, minuten(x.bis) - von);
                  const bahn = spuren.get(x.id) ?? { spur: 0, spuren: 1 };
                  return (
                    <button
                      key={x.id}
                      data-termin={x.id}
                      onClick={() => onOeffnen(x)}
                      className={`absolute overflow-hidden rounded border-s-4 p-1 text-start shadow-sm ${kachelfarbe(x)}`}
                      style={{
                        top: (von - beginn) * PX_JE_MINUTE,
                        /*
                         * Flacher als am Tagesbrett. Eine Viertelstunde ist
                         * hier siebzehn Pixel hoch; mit der Mindesthoehe des
                         * Tagesbretts stuende sie ueber dem naechsten Termin
                         * und zwei Kacheln laegen uebereinander.
                         */
                        height: Math.max(26, dauer * PX_JE_MINUTE - 2),
                        left: `${(bahn.spur / bahn.spuren) * 100}%`,
                        width: `${(1 / bahn.spuren) * 100}%`
                      }}
                    >
                      <p className="text-[10px] text-leise tabular-nums">{x.von}</p>
                      <p className="truncate text-xs font-bold">
                        {x.art === "pause"
                          ? t("plan.pause")
                          : x.ansprache ?? x.objekt ?? t("zeit.ohneObjekt")}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tagesansicht({
  tag, tage, termine, alleTermine, fahrten, spalten, alle, bedarf, istAdmin,
  onTag, onVerschieben, onOeffnen, onNeu, onAusrichten, onAnlegen
}: {
  tag: string;
  tage: string[];
  termine: Termin[];
  alleTermine: Termin[];
  fahrten: Fahrt[];
  spalten: Person[];
  /** Alle Mitarbeiter, auch die ohne Spalte. Fuer die Beifahrer der Ablage. */
  alle: Person[];
  bedarf: Bedarfszeile[];
  istAdmin: boolean;
  onTag: (tag: string) => void;
  onVerschieben: (id: string, aenderung: Verschiebung) => void;
  onOeffnen: (x: Termin) => void;
  onNeu: (benutzerId: string, von: string) => void;
  onAusrichten: (benutzerId: string, optimieren: boolean) => void;
  onAnlegen: (a: Anlage) => void;
}) {
  const { t, i18n } = useTranslation();
  const [zug, setZug] = useState<Zug | null>(null);
  const zugRef = useRef<Zug | null>(null);
  zugRef.current = zug;
  const [teamJeAbteilung, setTeamJeAbteilung] = useState<Record<string, number>>({});
  const [ablageFehler, setAblageFehler] = useState<string | null>(null);

  /*
   * Der Zeitraum des Bretts richtet sich nach den Terminen, mindestens aber
   * von sieben bis neunzehn Uhr. Ein Brett, das immer bei null Uhr beginnt,
   * verschenkt die Haelfte der Hoehe an eine Nacht, in der niemand arbeitet.
   */
  const [beginn, ende] = useMemo(() => zeitraum(termine), [termine]);

  /*
   * Die Fahrten des gezeigten Tages, nach Mitarbeiter sortiert. Ein Termin
   * steht in zwei Spalten, wenn zwei Leute hinfahren, und beide kommen von
   * einem anderen Ort her. Deshalb wird hier je Spalte nachgeschlagen und
   * nicht je Termin.
   */
  const fahrtenJeSpalte = useMemo(() => {
    const karte = new Map<string, Fahrt[]>();
    for (const f of fahrten) {
      if (f.datum !== tag) continue;
      const liste = karte.get(f.benutzerId);
      if (liste) liste.push(f);
      else karte.set(f.benutzerId, [f]);
    }
    return karte;
  }, [fahrten, tag]);

  const hoehe = (ende - beginn) * PX_JE_MINUTE;

  const mass = { beginn, ende, pxJeMinute: PX_JE_MINUTE, raster: RASTER };

  function zeitAus(y: number, kasten: DOMRect, griffY: number, dauer: number): number {
    return zielzeit(y, kasten.top, griffY, dauer, mass);
  }

  function beiZeiger(x: number, y: number) {
    const el = document.elementFromPoint(x, y);
    return {
      spalte: el?.closest<HTMLElement>("[data-spalte]") ?? null,
      tagchip: el?.closest<HTMLElement>("[data-tagchip]") ?? null
    };
  }

  function zeigerRunter(e: React.PointerEvent, x: Termin, quelleId: string) {
    if (!istAdmin || x.gestempelt) return;
    const kasten = (e.currentTarget as HTMLElement).getBoundingClientRect();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setZug({
      id: x.id,
      vorlage: null,
      quelleId,
      dauer: minuten(x.bis) - minuten(x.von),
      breite: kasten.width,
      hoehe: kasten.height,
      griffX: e.clientX - kasten.left,
      griffY: e.clientY - kasten.top,
      x: e.clientX,
      y: e.clientY,
      gezogen: false,
      titel: x.ansprache ?? x.objekt ?? "",
      zeit: null
    });
  }

  function zeigerBewegt(e: React.PointerEvent) {
    const z = zugRef.current;
    if (!z) return;
    const weit = Math.abs(e.clientX - z.x) > ZIEHSCHWELLE || Math.abs(e.clientY - z.y) > ZIEHSCHWELLE;
    const gezogen = z.gezogen || weit;
    let zeit: string | null = null;
    if (gezogen) {
      const { spalte } = beiZeiger(e.clientX, e.clientY);
      if (spalte) {
        zeit = alsUhrzeit(zeitAus(e.clientY, spalte.getBoundingClientRect(), z.griffY, z.dauer));
      }
    }
    setZug({ ...z, gezogen, x: e.clientX, y: e.clientY, zeit });
  }

  function zeigerHoch(e: React.PointerEvent, x: Termin) {
    const z = zugRef.current;
    setZug(null);
    if (!z) return;

    // Kaum bewegt: das war ein Klick, kein Zug.
    if (!z.gezogen) {
      onOeffnen(x);
      return;
    }

    const { spalte, tagchip } = beiZeiger(e.clientX, e.clientY);

    if (tagchip?.dataset.tagchip && tagchip.dataset.tagchip !== x.datum) {
      // Der ganze Termin zieht um, mit allen Beteiligten. Ein halbes Team am
      // Dienstag und die andere Haelfte am Mittwoch waere kein Umzug.
      onVerschieben(x.id, { datum: tagchip.dataset.tagchip });
      return;
    }
    if (spalte?.dataset.spalte) {
      const minute = zeitAus(e.clientY, spalte.getBoundingClientRect(), z.griffY, z.dauer);
      const von = alsUhrzeit(minute);
      const bis = alsUhrzeit(minute + z.dauer);
      const ziel = spalte.dataset.spalte;
      const aenderung: Verschiebung = { datum: x.datum };
      if (von !== x.von) { aenderung.von = von; aenderung.bis = bis; }
      /*
       * Zur Seite ersetzt genau den einen Menschen, aus dessen Spalte gezogen
       * wurde. Die uebrigen bleiben stehen: wer bei zwei Gaertnern einen
       * austauschen will, will nicht das Team aufloesen. Landet der Block in
       * der Spalte eines Kollegen, der ohnehin dabei ist, aendert sich an der
       * Besetzung nichts.
       */
      if (ziel !== z.quelleId && !x.mitglieder.some((m) => m.id === ziel)) {
        aenderung.benutzerIds = x.mitglieder.map((m) => (m.id === z.quelleId ? ziel : m.id));
      }
      if (aenderung.von || aenderung.benutzerIds) onVerschieben(x.id, aenderung);
    }
  }

  // ---------------- Ablage ----------------

  /**
   * Die offenen Leistungen der Woche, je Objekt gebuendelt.
   *
   * Ein Objekt ist eine Anfahrt, und die Bereiche daran gehoeren zusammen.
   * Sie einzeln untereinander zu listen wuerde sie so aussehen lassen wie
   * zwoelf getrennte Fahrten, obwohl es sechs Haeuser sind.
   */
  const ablage = useMemo(() => {
    const karte = new Map<string, { titel: string; zeilen: Bedarfszeile[] }>();
    for (const z of bedarf) {
      if (z.offen <= 0) continue;
      const eintrag = karte.get(z.objektId);
      if (eintrag) eintrag.zeilen.push(z);
      else karte.set(z.objektId, { titel: z.kunde ?? z.objekt, zeilen: [z] });
    }
    return [...karte.values()].sort((a, b) => a.titel.localeCompare(b.titel));
  }, [bedarf]);

  /**
   * Wer mit diesem Menschen im selben Auto sitzt.
   *
   * In beide Richtungen: der Beifahrer eines Fahrers und der Fahrer eines
   * Beifahrers. Sonst haenge die Zweierbesetzung daran, auf welche der beiden
   * Spalten man zufaellig zielt.
   */
  function beifahrer(id: string): Person | null {
    const ich = alle.find((p) => p.id === id);
    if (ich?.faehrtMitId) return alle.find((p) => p.id === ich.faehrtMitId) ?? null;
    return alle.find((p) => p.faehrtMitId === id) ?? null;
  }

  /** Die Dauer einer Vorlage: Personenminuten geteilt durch das Team, auf das Raster. */
  function vorlagendauer(offen: number, personen: number): number {
    return Math.max(RASTER, Math.round(offen / personen / RASTER) * RASTER);
  }

  function vorlageRunter(e: React.PointerEvent, v: Vorlage) {
    if (!istAdmin) return;
    setAblageFehler(null);
    const kasten = (e.currentTarget as HTMLElement).getBoundingClientRect();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setZug({
      id: null,
      vorlage: v,
      quelleId: "",
      dauer: vorlagendauer(v.offen, v.personen),
      breite: kasten.width,
      hoehe: kasten.height,
      griffX: e.clientX - kasten.left,
      griffY: e.clientY - kasten.top,
      x: e.clientX,
      y: e.clientY,
      gezogen: false,
      titel: v.titel,
      zeit: null
    });
  }

  function vorlageHoch(e: React.PointerEvent) {
    const z = zugRef.current;
    setZug(null);
    if (!z?.vorlage || !z.gezogen) return;

    const { spalte } = beiZeiger(e.clientX, e.clientY);
    if (!spalte?.dataset.spalte) return;
    const ziel = spalte.dataset.spalte;

    /*
     * Zu zweit heisst: der aus der Spalte und sein Beifahrer. Gibt es keinen,
     * wird der Termin nicht mit einem beliebigen Kollegen gefuellt und auch
     * nicht stillschweigend auf einen Mann umgerechnet, denn das waere der
     * doppelte Tag. Es wird gesagt, was fehlt.
     */
    let benutzerIds = [ziel];
    if (z.vorlage.personen === 2) {
      const zweiter = beifahrer(ziel);
      if (!zweiter) {
        setAblageFehler(t("plan.ablageKeinBeifahrer", {
          name: spalten.find((p) => p.id === ziel)?.display_name ?? ""
        }));
        return;
      }
      benutzerIds = [ziel, zweiter.id];
    }

    const minute = zeitAus(e.clientY, spalte.getBoundingClientRect(), z.griffY, z.dauer);
    onAnlegen({
      objektId: z.vorlage.objektId,
      abteilungId: z.vorlage.abteilungId,
      datum: tag,
      von: alsUhrzeit(minute),
      bis: alsUhrzeit(minute + z.dauer),
      benutzerIds
    });
  }

  const stundenmarken: number[] = [];
  for (let m = beginn; m <= ende; m += 60) stundenmarken.push(m);

  return (
    /*
     * Waehrend des Ziehens wird nichts markiert. Ohne diese Sperre faehrt der
     * Zeiger mit gedrueckter Taste ueber die Nachbarspalten und markiert deren
     * Text blau, quer ueber das halbe Brett. Das sieht aus wie ein Fehler und
     * lenkt genau in dem Augenblick ab, in dem man zielen will.
     */
    <div className={zug ? "select-none" : ""}>
      {/* Die Woche als Leiste. Sie waehlt den Tag und ist zugleich das Ziel,
          wenn ein Termin auf einen anderen Tag soll. */}
      <div className="mb-4 flex flex-wrap gap-2">
        {tage.map((x) => {
          const zahl = alleTermine.filter((y) => y.datum === x).length;
          const aktiv = x === tag;
          return (
            <button
              key={x}
              data-tagchip={x}
              onClick={() => onTag(x)}
              className={`min-h-11 rounded border px-3 text-sm font-semibold transition ${
                aktiv
                  ? "border-marke bg-marke text-white"
                  : zug?.gezogen
                    ? "border-dashed border-marke bg-white text-tinte"
                    : "border-linie/40 bg-white text-tinte hover:bg-grund"
              }`}
            >
              <span className={x === heute() && !aktiv ? "text-verweis" : ""}>
                {wochentag(x, i18n.language)}
              </span>{" "}
              <span className="tabular-nums">{kurzdatum(x, i18n.language)}</span>
              {zahl > 0 && <span className="ms-2 text-xs opacity-80">{zahl}</span>}
            </button>
          );
        })}
      </div>

      {istAdmin && (
        <p className="mb-3 hidden text-xs text-leise lg:block">{t("plan.ziehenHinweis")}</p>
      )}

      {/*
        * Was die Farben bedeuten.
        *
        * Kurz und immer sichtbar. Eine Legende, die man aufklappen muss, liest
        * niemand, und eine Farbe, deren Bedeutung man raten muss, ist keine
        * Auskunft, sondern Dekoration.
        */}
      <p className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-leise">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border-s-4 border-purple-600 bg-purple-50" />
          {t("plan.farbeLaeuft")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border-s-4 border-green-600 bg-green-50" />
          {t("plan.farbeBeendet")}
        </span>
      </p>

      {/* -------- Telefon: Liste je Mitarbeiter -------- */}
      <div className="space-y-6 lg:hidden">
        {spalten.map((p) => {
          const seine = termine
            .filter((x) => x.mitglieder.some((m) => m.id === p.id))
            .sort((a, b) => a.von.localeCompare(b.von));
          return (
            <section key={p.id}>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h2 className="font-bold">{p.display_name}</h2>
                <span className="text-xs text-leise">
                  {t("plan.termineZahl", { count: seine.length })}
                </span>
              </div>
              {seine.length === 0 ? (
                <p className="text-sm text-leise">{t("plan.frei")}</p>
              ) : (
                <ul className="space-y-2">
                  {seine.map((x) => (
                    <li key={x.id}>
                      <button
                        onClick={() => istAdmin && onOeffnen(x)}
                        className={`w-full rounded-card border-s-4 p-3 text-start shadow-sm ${kachelfarbe(x)}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs text-leise tabular-nums">
                            {x.von} bis {x.bis}
                          </span>
                          {x.gestempelt && (
                            <span className="text-xs text-leise">{t("plan.gestempelt")}</span>
                          )}
                        </div>
                        <p className="font-bold">
                          {x.art === "pause"
                            ? t("plan.pause")
                            : x.ansprache ?? x.objekt ?? t("zeit.ohneObjekt")}
                        </p>
                        {x.art === "pause" && (
                          <p className="text-sm text-leise">{t("plan.pauseHinweis")}</p>
                        )}
                        {x.ansprache && x.objekt && (
                          <p className="text-sm text-leise">{x.objekt}</p>
                        )}
                        <Teamzeile termin={x} ohne={p.id} />
                        {x.aenderung && (
                          <p className="mt-1 text-xs font-semibold text-red-700">
                            {x.aenderung}
                            {!x.aenderungGelesenAm && ` · ${t("plan.aenderungUngelesenKurz")}`}
                          </p>
                        )}
                        {x.notiz && <p className="mt-1 text-xs">{x.notiz}</p>}
                        <Konfliktzeile termin={x} />
                      </button>
                      <Fahrtzeile
                        fahrt={(fahrtenJeSpalte.get(p.id) ?? [])
                          .find((f) => f.vonTerminId === x.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {istAdmin && (
                <button
                  className="mt-2 min-h-11 w-full rounded border border-dashed border-linie/40 text-xs text-leise transition hover:bg-grund"
                  onClick={() => onNeu(p.id, "09:00")}
                >
                  {t("plan.neu")}
                </button>
              )}
            </section>
          );
        })}
      </div>

      {/* -------- Bildschirm: das Brett -------- */}
      <div className="hidden lg:block">
        <div className="flex">
          {/* Uhrzeitleiste */}
          <div className="w-14 shrink-0" style={{ height: hoehe }}>
            <div className="h-8" />
            <div className="relative" style={{ height: hoehe }}>
              {stundenmarken.map((m) => (
                <div
                  key={m}
                  className="absolute -translate-y-1/2 text-xs text-leise tabular-nums"
                  style={{ top: (m - beginn) * PX_JE_MINUTE }}
                >
                  {alsUhrzeit(m)}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-1 gap-2">
            {spalten.map((p) => {
              const seine = termine
                .filter((x) => x.mitglieder.some((m) => m.id === p.id))
                .sort((a, b) => a.von.localeCompare(b.von));
              const spuren = bahnen(seine);
              return (
                <div key={p.id} className="min-w-0 flex-1">
                  <div className="flex h-8 items-baseline justify-between gap-1">
                    <span className="truncate text-sm font-bold">{p.display_name}</span>
                    <div className="flex shrink-0 items-baseline gap-2">
                      {/*
                        * Ausrichten gibt es nur, wo es etwas auszurichten gibt,
                        * und nur fuer die Geschaeftsleitung. Ein Knopf, der an
                        * einem leeren Tag steht und nichts tut, wird beim
                        * naechsten Mal auch dann nicht gedrueckt, wenn er
                        * helfen wuerde.
                        */}
                      {istAdmin && seine.length > 1 && (
                        <Textknopf onClick={() => onAusrichten(p.id, false)}>
                          {t("plan.tagAusrichten")}
                        </Textknopf>
                      )}
                      {/*
                        * Erst ab drei Halten. Bei zweien gibt es nur eine
                        * Reihenfolge und ihre Umkehrung, beide gleich lang;
                        * ein Knopf, der nichts aendern kann, gehoert weg.
                        */}
                      {istAdmin && seine.length > 2 && (
                        <Textknopf onClick={() => onAusrichten(p.id, true)}>
                          {t("plan.routeOptimieren")}
                        </Textknopf>
                      )}
                      <span className="text-xs text-leise">{seine.length}</span>
                    </div>
                  </div>
                  <div
                    data-spalte={p.id}
                    className="relative rounded border border-linie bg-white"
                    style={{ height: hoehe }}
                    onClick={(e) => {
                      // Klick auf freie Flaeche legt dort einen Termin an.
                      if (!istAdmin) return;
                      if ((e.target as HTMLElement).closest("[data-termin]")) return;
                      const kasten = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const minute = begrenzen(
                        Math.round((beginn + (e.clientY - kasten.top) / PX_JE_MINUTE) / RASTER) * RASTER,
                        beginn,
                        ende - 60
                      );
                      onNeu(p.id, alsUhrzeit(minute));
                    }}
                  >
                    {stundenmarken.map((m) => (
                      <div
                        key={m}
                        className="pointer-events-none absolute inset-x-0 border-t border-linie"
                        style={{ top: (m - beginn) * PX_JE_MINUTE }}
                      />
                    ))}

                    {/*
                      * Die Fahrt liegt in der Luecke zwischen zwei Bloecken.
                      * Passt sie nicht hinein, wird sie rot: dann ist der Tag
                      * auf dem Papier voll und in Wirklichkeit gerissen.
                      */}
                    {(fahrtenJeSpalte.get(p.id) ?? []).map((f) => {
                      const vorher = seine.find((x) => x.id === f.vonTerminId);
                      const nachher = seine.find((x) => x.id === f.nachTerminId);
                      if (!vorher || !nachher) return null;
                      const start = minuten(vorher.bis);
                      const hoeheStreifen = Math.max(16, (minuten(nachher.von) - start) * PX_JE_MINUTE);
                      return (
                        <div
                          key={`${f.vonTerminId}-${f.nachTerminId}`}
                          title={t("plan.fahrtTitel", { km: f.km, minuten: f.minuten })}
                          className={`pointer-events-none absolute inset-x-0 flex items-center justify-center overflow-hidden rounded text-[10px] font-semibold ${
                            f.passt
                              ? "bg-grund/70 text-leise"
                              : "bg-red-100 text-red-700"
                          }`}
                          style={{ top: (start - beginn) * PX_JE_MINUTE, height: hoeheStreifen }}
                        >
                          <span className="truncate px-1 tabular-nums">
                            {t("plan.fahrtKurz", { minuten: f.minuten })}
                          </span>
                        </div>
                      );
                    })}

                    {seine.map((x) => {
                      const von = minuten(x.von);
                      const dauer = Math.max(RASTER, minuten(x.bis) - von);
                      const bahn = spuren.get(x.id) ?? { spur: 0, spuren: 1 };
                      const ziehbar = istAdmin && !x.gestempelt;
                      const imTeam = x.mitglieder.length > 1;
                      return (
                        <div
                          key={x.id}
                          data-termin={x.id}
                          onPointerDown={(e) => zeigerRunter(e, x, p.id)}
                          onPointerMove={zeigerBewegt}
                          onPointerUp={(e) => zeigerHoch(e, x)}
                          onPointerCancel={() => setZug(null)}
                          title={x.gestempelt ? t("plan.gestempelt") : undefined}
                          className={`absolute overflow-hidden rounded border-s-4 p-2 text-start shadow-sm ${
                            zug?.id === x.id && zug.gezogen ? "opacity-30" : ""
                          } ${kachelfarbe(x)} ${
                            !ziehbar
                              ? "cursor-default"
                              : zug?.gezogen
                                ? "cursor-grabbing touch-none"
                                : "cursor-grab touch-none"
                          }`}
                          style={{
                            top: (von - beginn) * PX_JE_MINUTE,
                            height: Math.max(MINDESTHOEHE, dauer * PX_JE_MINUTE - 2),
                            left: `${(bahn.spur / bahn.spuren) * 100}%`,
                            width: `${(1 / bahn.spuren) * 100}%`
                          }}
                        >
                          <div className="flex items-baseline justify-between gap-1">
                            <p className="text-[11px] text-leise tabular-nums">
                              {x.von} bis {x.bis}
                            </p>
                            {/* Im Team: wie viele. Ein Block, der in zwei
                                Spalten steht, sieht sonst aus wie zwei
                                verschiedene Termine. */}
                            {imTeam && (
                              <span className="shrink-0 rounded bg-marke/20 px-1 text-[10px] font-bold text-verweis">
                                {t("plan.teamZahl", { count: x.mitglieder.length })}
                              </span>
                            )}
                          </div>
                          <p className="truncate text-sm font-bold">
                            {x.art === "pause"
                              ? t("plan.pause")
                              : x.ansprache ?? x.objekt ?? t("zeit.ohneObjekt")}
                          </p>
                          {x.art === "pause" && (
                            <p className="truncate text-xs text-leise">
                              {t("plan.pauseHinweis")}
                            </p>
                          )}
                          {x.objekt && x.ansprache && (
                            <p className="truncate text-xs text-leise">{x.objekt}</p>
                          )}
                          {x.abteilung && (
                            <p className="truncate text-xs text-leise">{x.abteilung}</p>
                          )}
                          {x.aenderung && (
                            <p className="truncate text-xs font-semibold text-red-700">
                              {x.aenderungGelesenAm ? "" : "\u2022 "}{x.aenderung}
                            </p>
                          )}
                          {x.notiz && <p className="truncate text-xs">{x.notiz}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/*
            * Die Ablage: was diese Woche noch offen ist.
            *
            * Sie steht rechts neben den Spalten, weil sie in dieselbe Geste
            * gehoert wie das Brett: anfassen, hinueberziehen, loslassen. Als
            * eigene Ansicht waere sie eine Liste, die man abschreibt.
            *
            * Nur fuer die Geschaeftsleitung und nur auf dem grossen
            * Bildschirm, wie das Brett selbst. Auf dem Telefon gibt es die
            * Ansicht Bedarf.
            */}
          {istAdmin && (
            <aside className="ms-3 w-64 shrink-0">
              <div className="flex h-8 items-baseline justify-between gap-1">
                <span className="truncate text-sm font-bold">{t("plan.ablage")}</span>
                <span className="text-xs text-leise tabular-nums">{ablage.length}</span>
              </div>
              <div
                className="overflow-y-auto rounded border border-linie bg-white p-2"
                style={{ height: hoehe }}
              >
                {ablageFehler && (
                  <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700">
                    {ablageFehler}
                  </p>
                )}
                {ablage.length === 0 ? (
                  <p className="text-xs text-leise">{t("plan.ablageLeer")}</p>
                ) : (
                  <>
                    <p className="mb-2 text-[11px] text-leise">{t("plan.ablageHinweis")}</p>
                    <ul className="space-y-3">
                      {ablage.map((o) => (
                        <li key={o.zeilen[0].objektId}>
                          <p className="truncate text-xs font-bold">{o.titel}</p>
                          <ul className="mt-1 space-y-1">
                            {o.zeilen.map((z) => {
                              const personen = teamJeAbteilung[z.abteilungId] ?? 1;
                              const dauer = vorlagendauer(z.offen, personen);
                              const vorlage: Vorlage = {
                                objektId: z.objektId,
                                abteilungId: z.abteilungId,
                                titel: o.titel,
                                abteilung: z.abteilung,
                                offen: z.offen,
                                personen
                              };
                              return (
                                <li key={z.abteilungId} className="flex items-stretch gap-1">
                                  <div
                                    onPointerDown={(e) => vorlageRunter(e, vorlage)}
                                    onPointerMove={zeigerBewegt}
                                    onPointerUp={vorlageHoch}
                                    onPointerCancel={() => setZug(null)}
                                    className={`min-w-0 flex-1 rounded border-s-4 border-marke bg-grund/60 px-2 py-1 ${
                                      zug?.vorlage?.abteilungId === z.abteilungId
                                      && zug.vorlage.objektId === z.objektId
                                      && zug.gezogen
                                        ? "opacity-30"
                                        : ""
                                    } ${zug?.gezogen ? "cursor-grabbing touch-none" : "cursor-grab touch-none"}`}
                                  >
                                    <p className="truncate text-xs font-semibold">{z.abteilung}</p>
                                    <p className="text-[11px] text-leise tabular-nums">
                                      {t("plan.ablageZeile", {
                                        offen: alsStunden(z.offen),
                                        dauer: alsStunden(dauer)
                                      })}
                                    </p>
                                  </div>
                                  {/*
                                    * Ein oder zwei Personen. Die Wahl gilt fuer
                                    * alle Zeilen derselben Abteilung, denn sie
                                    * gehoert zur Art der Arbeit und nicht zum
                                    * einzelnen Haus: Gaerten macht das Paar,
                                    * Pools macht einer.
                                    */}
                                  <div className="flex shrink-0 flex-col overflow-hidden rounded border border-linie/40">
                                    {[1, 2].map((n) => (
                                      <button
                                        key={n}
                                        type="button"
                                        title={n === 1 ? t("plan.ablageEiner") : t("plan.ablageZwei")}
                                        onClick={() => setTeamJeAbteilung(
                                          (alt) => ({ ...alt, [z.abteilungId]: n })
                                        )}
                                        className={`w-6 flex-1 text-[11px] font-bold ${
                                          personen === n
                                            ? "bg-marke text-white"
                                            : "bg-white text-leise hover:bg-grund"
                                        }`}
                                      >
                                        {n}
                                      </button>
                                    ))}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Der Schatten am Zeiger. Er zeigt die Zielzeit, damit niemand raten
          muss, wo der Termin landet. */}
      {zug?.gezogen && (
        <div
          className="pointer-events-none fixed z-40 rounded border-s-4 border-marke bg-white p-2 shadow-lg"
          style={{
            left: zug.x - zug.griffX,
            top: zug.y - zug.griffY,
            width: zug.breite,
            height: zug.hoehe
          }}
        >
          <p className="text-[11px] font-semibold text-verweis tabular-nums">
            {zug.zeit ?? "—"}
          </p>
          <p className="truncate text-sm font-bold">{zug.titel}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Die Fahrt zum naechsten Termin, auf dem Telefon.
 *
 * Auf dem kleinen Bildschirm gibt es keine Luecke, in die man einen Streifen
 * legen koennte, also steht sie als Zeile zwischen den Karten. Passt sie
 * nicht, ist sie rot, denn dann stimmt der Tag nicht.
 */
function Fahrtzeile({ fahrt }: { fahrt: Fahrt | undefined }) {
  const { t } = useTranslation();
  if (!fahrt) return null;
  return (
    <p
      className={`mt-1 text-xs tabular-nums ${
        fahrt.passt ? "text-leise" : "font-semibold text-red-700"
      }`}
    >
      {t("plan.fahrtLang", { km: fahrt.km, minuten: fahrt.minuten })}
      {/*
        * Nur bei echter Luecke. Ueberschneiden sich zwei Termine, ist die
        * Luecke negativ, und "nur -90 Minuten Luft" liest sich wie ein Fehler
        * der Anwendung. Dass sich die Termine ueberschneiden, sagt ohnehin
        * schon der Konflikt an der Karte darueber.
        */}
      {!fahrt.passt && fahrt.luecke > 0
        && ` · ${t("plan.fahrtZuKnapp", { count: fahrt.luecke })}`}
    </p>
  );
}

/** Wer sonst noch mitfaehrt. Auf dem Telefon steht der eigene Name nicht dabei. */
function Teamzeile({ termin, ohne }: { termin: Termin; ohne?: string }) {
  const { t } = useTranslation();
  const andere = termin.mitglieder.filter((m) => m.id !== ohne);
  if (andere.length === 0) return null;
  return (
    <p className="mt-1 text-xs font-semibold text-verweis">
      {t("plan.gemeinsamMit", { namen: andere.map((m) => m.name).join(", ") })}
    </p>
  );
}

function Konfliktzeile({ termin }: { termin: Termin }) {
  const { t } = useTranslation();
  if (!termin.konflikt) return null;
  const art = termin.konflikt.art;
  return (
    <p
      className={`mt-1 text-xs font-semibold ${
        art === "feiertag" || art === "fachfremd" ? "text-verweis" : "text-red-700"
      }`}
    >
      {art === "abwesenheit"
        ? t("plan.konfliktAbwesenheit", { art: t(`antrag.art.${termin.konflikt.hinweis}`) })
        : art === "ueberschneidung"
          ? t("plan.konfliktUeberschneidung")
          : art === "fachfremd"
            ? t("plan.konfliktFachfremd", { abteilung: termin.konflikt.hinweis })
            : t("plan.konfliktFeiertag", { name: termin.konflikt.hinweis })}
    </p>
  );
}

// ---------------------------------------------------------------------
// Anlegen und Aendern
// ---------------------------------------------------------------------

type Dialogauftrag =
  | { art: "neu"; tag: string; benutzerId: string; von: string }
  | { art: "aendern"; termin: Termin };

function TerminDialog({
  auftrag, personen, objekte, abteilungen, onClose, onFertig, onLoeschen
}: {
  auftrag: Dialogauftrag;
  personen: Person[];
  objekte: Objekt[];
  abteilungen: Abteilung[];
  onClose: () => void;
  onFertig: () => void;
  onLoeschen: (id: string) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const vorhanden = auftrag.art === "aendern" ? auftrag.termin : null;

  const [benutzerIds, setBenutzerIds] = useState<string[]>(
    vorhanden
      ? vorhanden.mitglieder.map((m) => m.id)
      : [auftrag.art === "neu" ? auftrag.benutzerId : personen[0]?.id ?? ""].filter(Boolean)
  );
  const [datum, setDatum] = useState(vorhanden?.datum ?? (auftrag.art === "neu" ? auftrag.tag : ""));
  const [objektId, setObjektId] = useState(vorhanden?.objektId ?? "");
  const [bereiche, setBereiche] = useState<Bereich[]>(vorhanden?.bereiche ?? []);
  const [von, setVon] = useState(vorhanden?.von ?? (auftrag.art === "neu" ? auftrag.von : "09:00"));
  const [bis, setBis] = useState(
    vorhanden?.bis ?? (auftrag.art === "neu" ? alsUhrzeit(minuten(auftrag.von) + 90) : "11:00")
  );
  const [notiz, setNotiz] = useState(vorhanden?.notiz ?? "");
  const [aenderung, setAenderung] = useState(vorhanden?.aenderung ?? "");
  const [wiederholungen, setWiederholungen] = useState(1);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  /*
   * Die Arbeitsstunden, die dieser Termin erbringt: Dauer mal Anzahl. Genau
   * diese Zahl steht als Sollzeit am Objekt, deshalb muss sie schon beim
   * Planen sichtbar sein und nicht erst im Bericht.
   */
  const dauer = Math.max(0, minuten(bis) - minuten(von));
  const personenminuten = dauer * benutzerIds.length;
  const bereichsSumme = bereiche.reduce((s, b) => s + b.minuten, 0);
  // Ohne Bereich ist es schlicht ein Besuch, das bleibt erlaubt. Mit Bereichen
  // muessen die Anteile die Dauer ergeben, sonst zaehlt am Objekt eine Sollzeit
  // als erfuellt, die niemand gearbeitet hat.
  const summeStimmt = bereiche.length === 0 || bereichsSumme === dauer;

  function umschalten(id: string) {
    setBenutzerIds((alt) => alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]);
  }

  /** Einen Bereich dazunehmen oder herausnehmen, danach neu aufteilen. */
  function bereichUmschalten(a: Abteilung) {
    setBereiche((alt) => verteilen(
      alt.some((b) => b.abteilungId === a.id)
        ? alt.filter((b) => b.abteilungId !== a.id)
        : [...alt, { abteilungId: a.id, abteilung: a.name, minuten: 0 }],
      dauer
    ));
  }

  /** Die Uhrzeit aendern und die Anteile auf die neue Dauer umrechnen. */
  function zeitAendern(neuVon: string, neuBis: string) {
    setVon(neuVon);
    setBis(neuBis);
    const neueDauer = Math.max(0, minuten(neuBis) - minuten(neuVon));
    setBereiche((alt) => skaliert(alt, neueDauer));
  }

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      const koerper = {
        benutzerIds,
        objektId: objektId || null,
        bereiche: bereiche.map((b) => ({ abteilungId: b.abteilungId, minuten: b.minuten })),
        datum, von, bis, notiz: notiz || null,
        aenderung: aenderung.trim() || null
      };
      if (vorhanden) {
        await api.patch(`/termine/${vorhanden.id}`, koerper);
      } else if (wiederholungen > 1) {
        await api.post("/termine/serie", { ...koerper, wiederholungen, abstandWochen: 1 });
      } else {
        await api.post("/termine", koerper);
      }
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">
          {vorhanden ? t("plan.terminAendern") : t("plan.neuerTermin", { tag: datum })}
        </h2>
        <Fehler text={fehler} />

        {/*
          * Das Team als Liste zum Ankreuzen und nicht als Auswahlfeld mit
          * gedrueckter Steuerungstaste: Letzteres kennt auf dem Telefon
          * niemand, und wer sich vertippt, loescht damit still die ganze
          * Besetzung.
          */}
        <Feld label={t("plan.team")} hinweis={t("plan.teamHinweis")}>
          <div className="flex flex-wrap gap-2">
            {personen.map((p) => {
              const drin = benutzerIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => umschalten(p.id)}
                  className={`min-h-11 rounded border px-3 text-sm font-semibold transition ${
                    drin
                      ? "border-marke bg-marke text-white"
                      : "border-linie/40 bg-white text-tinte hover:bg-grund"
                  }`}
                >
                  {p.display_name}
                </button>
              );
            })}
          </div>
        </Feld>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Feld label={t("zeit.objektOptional")}>
            <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              <option value="">{t("zeit.ohneObjekt")}</option>
              {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
                <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
              ))}
            </Auswahl>
          </Feld>
          <Feld label={t("plan.datum")}>
            <Eingabe type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </Feld>
          <Feld label={t("zeit.beginn")}>
            <Eingabe
              type="time" value={von}
              onChange={(e) => zeitAendern(e.target.value, bis)}
            />
          </Feld>
          <Feld label={t("zeit.ende")}>
            <Eingabe
              type="time" value={bis}
              onChange={(e) => zeitAendern(von, e.target.value)}
            />
          </Feld>
          {!vorhanden && (
            <Feld label={t("plan.wiederholungen")} hinweis={t("plan.wiederholungenHinweis")}>
              <Eingabe
                type="number" min={1} max={52} value={wiederholungen}
                onChange={(e) => setWiederholungen(Math.max(1, Number(e.target.value) || 1))}
              />
            </Feld>
          )}
          <Feld label={t("plan.notiz")} hinweis={t("plan.notizHinweis")} breit>
            <Eingabe value={notiz} onChange={(e) => setNotiz(e.target.value)} />
          </Feld>
          {/*
            * Die Aenderung fuer diesen Tag. Rot, weil sie beim Mitarbeiter rot
            * ankommt, und getrennt von der Notiz, damit die Dauerinfo zum
            * Objekt nicht mitleuchtet. Wer den Termin nur verschiebt, laesst
            * das Feld stehen; die Lesequittung bleibt dann erhalten.
            */}
          <Feld label={t("plan.aenderung")} hinweis={t("plan.aenderungHinweis")} breit>
            <Eingabe value={aenderung} onChange={(e) => setAenderung(e.target.value)} />
          </Feld>
          {vorhanden?.aenderung && (
            <p className="text-xs text-leise sm:col-span-2">
              {vorhanden.aenderungGelesenAm
                ? t("plan.aenderungGelesen", {
                    name: vorhanden.aenderungGelesenVon ?? "",
                    zeit: new Date(vorhanden.aenderungGelesenAm)
                      .toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                  })
                : t("plan.aenderungUngelesen")}
            </p>
          )}
        </div>

        {/*
          * Die Bereiche des Besuchs.
          *
          * Als Knoepfe und nicht als Auswahlfeld, denn es sind mehrere: Pool
          * und Hauskontrolle an einer Fahrt sind der Regelfall und nicht die
          * Ausnahme. Die Minuten erscheinen erst ab dem zweiten Bereich, denn
          * bei einem einzigen ist der Anteil die Dauer und eine Eingabe, die
          * nur einen Wert zulaesst, ist keine.
          */}
        {abteilungen.length > 0 && (
          <div className="mt-3">
            <Feld label={t("plan.bereiche")} hinweis={t("plan.bereicheHinweis")}>
              <div className="flex flex-wrap gap-2">
                {abteilungen.map((a) => {
                  const drin = bereiche.some((b) => b.abteilungId === a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => bereichUmschalten(a)}
                      className={`min-h-11 rounded border px-3 text-sm font-semibold transition ${
                        drin
                          ? "border-marke bg-marke text-white"
                          : "border-linie/40 bg-white text-tinte hover:bg-grund"
                      }`}
                    >
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </Feld>

            {bereiche.length > 1 && (
              <div className="mt-3 space-y-2">
                {bereiche.map((b) => (
                  <div key={b.abteilungId} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 text-sm">{b.abteilung}</span>
                    <Eingabe
                      type="number" min={1} max={1440} value={b.minuten}
                      onChange={(e) => {
                        const wert = Math.max(0, Number(e.target.value) || 0);
                        setBereiche((alt) => alt.map((x) =>
                          x.abteilungId === b.abteilungId ? { ...x, minuten: wert } : x
                        ));
                      }}
                    />
                    <span className="text-sm text-leise">{t("plan.minuten")}</span>
                  </div>
                ))}
                <p className={`text-sm ${summeStimmt ? "text-leise" : "font-semibold text-red-700"}`}>
                  {t("plan.bereicheSumme", { summe: bereichsSumme, dauer })}
                </p>
              </div>
            )}
          </div>
        )}

        <p className="mt-3 text-sm text-leise">
          {t("plan.arbeitsstunden", {
            stunden: alsStunden(personenminuten),
            count: benutzerIds.length
          })}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Knopf
            disabled={laeuft || benutzerIds.length === 0 || !summeStimmt}
            onClick={() => void speichern()}
          >
            {t("aktion.speichern")}
          </Knopf>
          <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
          {vorhanden && (
            <div className="ms-auto">
              {/* Ein Termin, an dem gestempelt wurde, bleibt stehen: die
                  Buchung haengt daran. Die API weist es ohnehin ab, aber der
                  Knopf soll gar nicht erst einladen. */}
              <Textknopf
                gefahr
                disabled={laeuft || vorhanden.gestempelt}
                title={vorhanden.gestempelt ? t("plan.gestempelt") : undefined}
                onClick={() => {
                  if (window.confirm(t("plan.loeschenSicher"))) void onLoeschen(vorhanden.id);
                }}
              >
                {t("aktion.loeschen")}
              </Textknopf>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------

/**
 * Die Dauer gleichmaessig auf die Bereiche verteilen.
 *
 * Der Rest der Division landet beim ersten. Wer es genauer will, tippt es
 * daneben ein; eine Vorbelegung, die schon stimmt, ist mehr wert als eine, die
 * lange rechnet.
 */
function verteilen(bereiche: Bereich[], dauer: number): Bereich[] {
  if (bereiche.length === 0) return bereiche;
  const je = Math.floor(dauer / bereiche.length);
  const rest = dauer - je * bereiche.length;
  return bereiche.map((b, i) => ({ ...b, minuten: Math.max(0, je + (i === 0 ? rest : 0)) }));
}

/**
 * Die Anteile auf eine neue Dauer umrechnen.
 *
 * Dieselbe Regel wie in der API: wer einen Termin laenger zieht, meint, dass
 * alles etwas laenger dauert. Der Rundungsrest geht an den groessten Bereich.
 */
function skaliert(bereiche: Bereich[], dauer: number): Bereich[] {
  const summe = bereiche.reduce((s, b) => s + b.minuten, 0);
  if (bereiche.length === 0 || summe === dauer) return bereiche;
  // Ohne bisherige Anteile gibt es nichts umzurechnen, dann wird gleich geteilt.
  if (summe === 0) return verteilen(bereiche, dauer);
  const neu = bereiche.map((b) => ({
    ...b, minuten: Math.max(1, Math.round((b.minuten * dauer) / summe))
  }));
  const rest = dauer - neu.reduce((s, b) => s + b.minuten, 0);
  let groesster = 0;
  for (let i = 1; i < neu.length; i += 1) {
    if (neu[i].minuten > neu[groesster].minuten) groesster = i;
  }
  neu[groesster].minuten = Math.max(1, neu[groesster].minuten + rest);
  return neu;
}

/** Montag der Woche, in der dieses Datum liegt. */
function wochenanfang(d: Date): string {
  const kopie = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // getUTCDay: Sonntag ist 0. Die Woche beginnt hier am Montag, also wird der
  // Sonntag um sechs Tage zurueckgeschoben statt um null.
  const versatz = (kopie.getUTCDay() + 6) % 7;
  kopie.setUTCDate(kopie.getUTCDate() - versatz);
  return kopie.toISOString().slice(0, 10);
}

function plusTage(tag: string, tage: number): string {
  const [j, m, d] = tag.split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, d + tage)).toISOString().slice(0, 10);
}

function wochentag(tag: string, sprache: string): string {
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString(sprache, { weekday: "short" });
}

function kurzdatum(tag: string, sprache: string): string {
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString(sprache, {
    day: "2-digit", month: "2-digit"
  });
}
